/**
 * Admin Redemption Catalog Component (Master Admin Only)
 *
 * Allows master admin to manage the redemption catalog:
 * - Add frames, backgrounds, event cards, subscriptions with point costs
 * - Set featured items, stock limits, redemption limits
 * - Toggle active/inactive status
 * - Edit and delete catalog items
 */

Vue.component('admin-redemption-catalog', {
    template: templates.adminRedemptionCatalog,
    data() {
        return {
            model: {
                catalogItems: [],
                catalogItem: {
                    item_type: 'frame',
                    item_id: '',
                    name: '',
                    description: '',
                    point_cost: 0,
                    is_featured: false,
                    requires_approval: true,
                    featured_until: null,
                    stock_quantity: null,
                    max_redemptions_per_user: 1,
                    subscription_duration_days: null
                },
                editMode: false,

                // Available items for selection
                availableFrames: [],
                availableBackgrounds: [],
                availableCards: [],
                availableExclusiveCards: [],

                loading: false,
                error: null
            }
        };
    },
    computed: {
        availableItemsForType() {
            const c = this;
            switch(c.model.catalogItem.item_type) {
                case 'frame':
                    return c.model.availableFrames;
                case 'background':
                    return c.model.availableBackgrounds;
                case 'event_card':
                    return c.model.availableCards;
                case 'exclusive_card':
                    return c.model.availableExclusiveCards;
                default:
                    return [];
            }
        },
        isSubscriptionType() {
            return this.model.catalogItem.item_type === 'subscription';
        }
    },
    mounted() {
        const c = this;
        c.loadCatalog();
        c.loadAvailableItems();
    },
    methods: {
        loadCatalog() {
            const c = this;
            c.model.loading = true;

            redemptionService.getCatalog()
                .then(function (response) {
                    c.model.catalogItems = response.data.catalog;
                    c.model.loading = false;
                })
                .catch(function (error) {
                    console.error('Failed to load catalog:', error);
                    c.model.error = 'Failed to load catalog';
                    c.model.loading = false;
                });
        },

        loadAvailableItems() {
            const c = this;

            // Load available frames using frameService
            frameService.get()
                .then(function (response) {
                    c.model.availableFrames = response.data;
                })
                .catch(function (error) {
                    console.error('Failed to load frames:', error);
                });

            // Load available backgrounds using backgroundService
            backgroundService.get()
                .then(function (response) {
                    c.model.availableBackgrounds = response.data;
                })
                .catch(function (error) {
                    console.error('Failed to load backgrounds:', error);
                });

            // Load available cards for event cards using cardService
            cardService.get()
                .then(function (response) {
                    c.model.availableCards = response.data;
                })
                .catch(function (error) {
                    console.error('Failed to load cards:', error);
                });

            // Load available exclusive cards
            exclusiveCardService.get()
                .then(function (response) {
                    c.model.availableExclusiveCards = response.data;
                })
                .catch(function (error) {
                    console.error('Failed to load exclusive cards:', error);
                });
        },

        addItem() {
            const c = this;
            c.model.editMode = true;
            c.resetForm();
        },

        editItem(item) {
            const c = this;
            c.model.editMode = true;

            // Convert featured_until to datetime-local format if it exists
            let featuredUntil = null;
            if (item.featured_until) {
                // Convert MySQL datetime to ISO string format for datetime-local input
                const date = new Date(item.featured_until);
                featuredUntil = date.toISOString().slice(0, 16);
            }

            c.model.catalogItem = {
                id: item.id,
                item_type: item.item_type,
                item_id: item.item_id,
                name: item.name,
                description: item.description || '',
                point_cost: item.point_cost,
                is_featured: item.is_featured == 1,
                requires_approval: item.requires_approval == 1,
                featured_until: featuredUntil,
                stock_quantity: item.stock_quantity,
                max_redemptions_per_user: item.max_redemptions_per_user,
                subscription_duration_days: item.subscription_duration_days
            };
        },

        saveItem() {
            const c = this;
            const data = c.model.catalogItem;

            // Validation
            if (!data.name) {
                alert('Name is required');
                return;
            }

            if (!data.point_cost || data.point_cost <= 0) {
                alert('Point cost must be greater than 0');
                return;
            }

            if (data.item_type !== 'subscription' && !data.item_id) {
                alert('Please select an item');
                return;
            }

            if (data.item_type === 'subscription' && (!data.subscription_duration_days || data.subscription_duration_days <= 0)) {
                alert('Subscription duration is required');
                return;
            }

            c.model.loading = true;

            // Use appropriate service method
            const saveMethod = data.id
                ? redemptionService.updateCatalogItem(data)
                : redemptionService.addCatalogItem(data);

            saveMethod
                .then(function (response) {
                    if (response.data.success) {
                        alert('Catalog item saved successfully');
                        c.loadCatalog();
                        c.cancelEdit();
                    } else {
                        alert(response.data.message || 'Failed to save');
                    }
                    c.model.loading = false;
                })
                .catch(function (error) {
                    console.error('Save error:', error);
                    alert('Failed to save catalog item');
                    c.model.loading = false;
                });
        },

        toggleStatus(item) {
            const c = this;

            const newStatus = item.is_active == 1 ? 0 : 1;
            const action = newStatus == 1 ? 'activate' : 'deactivate';

            if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} "${item.name}"?`)) {
                return;
            }

            redemptionService.toggleCatalogItem(item.id, newStatus)
                .then(function (response) {
                    if (response.data.success) {
                        alert('Item status updated');
                        c.loadCatalog();
                    } else {
                        alert('Failed to update status');
                    }
                })
                .catch(function (error) {
                    console.error('Toggle error:', error);
                    alert('Failed to update status');
                });
        },

        deleteItem(item) {
            const c = this;

            if (!confirm(`Delete "${item.name}" from catalog?\n\nThis action cannot be undone.`)) {
                return;
            }

            redemptionService.deleteCatalogItem(item.id)
                .then(function (response) {
                    if (response.data.success) {
                        alert('Item deleted successfully');
                        c.loadCatalog();
                    } else {
                        alert('Failed to delete item');
                    }
                })
                .catch(function (error) {
                    console.error('Delete error:', error);
                    alert('Failed to delete item');
                });
        },

        cancelEdit() {
            const c = this;
            c.model.editMode = false;
            c.resetForm();
        },

        resetForm() {
            const c = this;
            c.model.catalogItem = {
                item_type: 'frame',
                item_id: '',
                name: '',
                description: '',
                point_cost: 0,
                is_featured: false,
                requires_approval: true,
                featured_until: null,
                stock_quantity: null,
                max_redemptions_per_user: 1,
                subscription_duration_days: null
            };
        },

        onItemTypeChange() {
            const c = this;
            // Reset item_id when type changes
            c.model.catalogItem.item_id = '';
        },

        getItemTypeLabel(itemType) {
            const labels = {
                'frame': 'Frame',
                'background': 'Background',
                'event_card': 'Event Card',
                'exclusive_card': 'Exclusive Streamer Card',
                'subscription': 'Subscription'
            };
            return labels[itemType] || itemType;
        },

        getStatusBadgeClass(item) {
            return (item.is_active === true || item.is_active == 1) ? 'badge-success' : 'badge-inactive';
        },

        getStatusLabel(item) {
            return (item.is_active === true || item.is_active == 1) ? 'Active' : 'Inactive';
        },

        formatFeaturedUntil(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        }
    }
});
